import { supabase } from "../lib/supabase";

/**
 * Clean attachment data to only include metadata (remove large file content)
 * @param {Array} attachments - Array of attachment objects
 * @returns {Array} - Cleaned attachments with only metadata
 */
const cleanAttachmentData = (attachments) => {
  if (!Array.isArray(attachments)) {
    return attachments;
  }
  
  return attachments.map(attachment => {
    if (!attachment || typeof attachment !== 'object') {
      return attachment;
    }
    
    // Keep only essential metadata, remove large data properties
    const cleanedAttachment = {
      id: attachment.id,
      name: attachment.name || attachment.filename,
      filename: attachment.filename || attachment.name,
      url: attachment.url,
      size: attachment.size,
      type: attachment.type || attachment.contentType,
      lastModified: attachment.lastModified,
      webkitRelativePath: attachment.webkitRelativePath,
      // Remove potential large data properties
      // data: removed (can be very large base64 data)
      // content: removed (can be large file content)
      // file: removed (can be large File object)
      // arrayBuffer: removed (can be large binary data)
    };
    
    // Remove undefined values to keep the object clean
    return Object.fromEntries(
      Object.entries(cleanedAttachment).filter(([key, value]) => value !== undefined)
    );
  });
};

export class DiscrepancyService {
  /**
   * Fetch all discrepancies with related project and client data
   * @returns {Promise<Array>} Array of discrepancies with relationships
   */
  static async getAllDiscrepancies() {
    try {
      // Step 1: Test basic discrepancy fetch with correct column names
      const { data: basicData, error: basicError } = await supabase
        .from("discrepancies")
        .select("id, name, status, type, created_at, project_id");

      if (basicError) {
        console.error("Step 1 failed:", basicError);
        throw basicError;
      }
      // Step 2: Test projects table access
      const { error: projectsError } = await supabase
        .from("projects")
        .select("id, name, job_number")
        .limit(1);

      if (projectsError) {
        console.error("Step 2 failed:", projectsError);
        throw projectsError;
      }
      // Step 3: Test simple join with correct column names
      const { data: joinData, error: joinError } = await supabase
        .from("discrepancies")
        .select(
          `
          id,
          name,
          status,
          type,
          created_at,
          project_id,
          projects(id, name, job_number)
        `,
        )
        .limit(3);

      if (joinError) {
        console.error("Step 3 failed:", joinError);
        console.error("Error details:", JSON.stringify(joinError, null, 2));

        // Fallback: return basic data without joins
        return (
          basicData?.map((item) => ({
            id: item.id,
            discrepancyName: item.name || "Unnamed Discrepancy",
            discrepancyStatus: item.status,
            discrepancyType: item.type,
            projectName: "Unknown Project",
            projectId: item.project_id,
            createdAt: item.created_at,
          })) || []
        );
      }
      // Step 4: Test the full query with nested joins
      const { data: fullData, error: fullError } = await supabase
        .from("discrepancies")
        .select(
          `
          *,
          markup_attachment,
          file_share_link,
          share_url,
          loom_link,
          created_by_name,
          sent_date,
          airtable_last_modified,
          projects(
            id,
            name,
            job_number,
            rev,
            modeling_status,
            modeling_state,
            clients(id, name),
            contacts(id, name, email, phone_number)
          )
        `,
        )
        .order("created_at", { ascending: false });

      if (fullError) {
        console.error("Step 4 failed:", fullError);
        console.error(
          "Full error details:",
          JSON.stringify(fullError, null, 2),
        );

        // Return Step 3 data if available
        if (joinData) {
          return joinData.map((item) => ({
            id: item.id,
            discrepancyName: item.name || "Unnamed Discrepancy",
            discrepancyStatus: item.status,
            discrepancyType: item.type,
            projectName: item.projects?.name || "Unknown Project",
            projectId: item.project_id,
            createdAt: item.created_at,
            project: item.projects
              ? {
                  id: item.projects.id,
                  projectName: item.projects.name,
                  jobNumber: item.projects.job_number,
                }
              : null,
          }));
        }
      }
      // Debug markup_attachment data
      if (fullData && fullData.length > 0) {
        const sampleWithMarkup = fullData.find(
          (item) => item.markup_attachment,
        );
        if (sampleWithMarkup) {
          console.log(
            "Is Array?:",
            Array.isArray(sampleWithMarkup.markup_attachment),
          );
          if (
            Array.isArray(sampleWithMarkup.markup_attachment) &&
            sampleWithMarkup.markup_attachment.length > 0
          ) {
            console.log(
              "Keys in first item:",
              Object.keys(sampleWithMarkup.markup_attachment[0]),
            );
          }
        } else {
        }
      }
      // Transform the successful data
      return (fullData || joinData || basicData || []).map((item) => ({
        id: item.id,
        discrepancyName: item.name || "Unnamed Discrepancy",
        discrepancyStatus: item.status,
        discrepancyType: item.type,
        description: item.additional_information,
        additionalInformation: item.additional_information,
        projectId: item.project_id,
        // Removed fields that don't exist in database: location, reportedBy, assignedTo, priority, resolution, notes, attachments
        markupAttachment: item.markup_attachment,
        fileShareLink: item.file_share_link,
        shareUrl: item.share_url,
        loomLink: item.loom_link,
        additionalContactIds: item.additional_contact_ids || [],
        createdByName: item.created_by_name,
        sentDate: item.sent_date,
        lastModifiedBy: item.last_modified_by,
        emailPreview: item.email_preview, // Add email_preview field
        sendEmail: item.send_email,
        putProjectOnHold: item.put_project_on_hold,
        holdReason: item.hold_reason,
        holdNotes: item.hold_notes,
        holdInitialFollowUpDays: item.hold_initial_follow_up_days,
        holdRecurringFollowUpDays: item.hold_recurring_follow_up_days,
        airtableLastModified: item.airtable_last_modified,
        createdAt: item.created_at,
        updatedAt: item.updated_at,
        createdBy: item.created_by,
        updatedBy: item.updated_by,
        project: item.projects
          ? {
              id: item.projects.id,
              projectName: item.projects.name,
              jobNumber: item.projects.job_number,
              rev: item.projects.rev,
              modelingStatus: item.projects.modeling_status,
              modelingState: item.projects.modeling_state,
              client: item.projects.clients
                ? {
                    id: item.projects.clients.id,
                    name: item.projects.clients.name,
                  }
                : null,
              contact: item.projects.contacts
                ? {
                    id: item.projects.contacts.id,
                    name: item.projects.contacts.name,
                    email: item.projects.contacts.email,
                    phoneNumber: item.projects.contacts.phone_number,
                  }
                : null,
            }
          : null,
      }));
    } catch (error) {
      console.error("=== QUERY DEBUG FAILED ===");
      console.error("Error:", error);
      throw error;
    }
  }

  /**
   * Get a single discrepancy by ID
   * @param {number} discrepancyId - The discrepancy ID
   * @returns {Promise<object>} Discrepancy with relationships
   */
  static async getDiscrepancyById(discrepancyId) {
    try {
      const { data, error } = await supabase
        .from("discrepancies")
        .select(
          `
          *,
          project:projects!project_id (
            id,
            name,
            job_number,
            rev,
            client:clients!client_id (
              id,
              name
            ),
            contact:contacts!contact_id (
              id,
              name,
              email,
              phone_number
            )
          )
        `,
        )
        .eq("id", discrepancyId)
        .single();

      if (error) {
        throw new Error(`Failed to fetch discrepancy: ${error.message}`);
      }

      if (!data) {
        return null;
      }

      // Transform to camelCase
      return {
        id: data.id,
        discrepancyName: data.name,
        discrepancyStatus: data.status,
        discrepancyType: data.type,
        description: data.additional_information,
        additionalInformation: data.additional_information,
        projectId: data.project_id,
        markupAttachment: data.markup_attachment,
        shareUrl: data.share_url,
        loomLink: data.loom_link,
        createdByName: data.created_by_name,
        sentDate: data.sent_date,
        lastModifiedBy: data.last_modified_by,
        emailPreview: data.email_preview, // Add email_preview field
        sendEmail: data.send_email,
        putProjectOnHold: data.put_project_on_hold,
        holdReason: data.hold_reason,
        holdNotes: data.hold_notes,
        holdInitialFollowUpDays: data.hold_initial_follow_up_days,
        holdRecurringFollowUpDays: data.hold_recurring_follow_up_days,
        // Removed fields that don't exist in database: location, reportedBy, assignedTo, priority, resolution, notes, attachments
        createdAt: data.created_at,
        updatedAt: data.updated_at,
        createdBy: data.created_by,
        updatedBy: data.updated_by,
        project: data.project
          ? {
              id: data.project.id,
              projectName: data.project.name,
              jobNumber: data.project.job_number,
              rev: data.project.rev,
              clientName: data.project.client_name,
              client: data.project.client
                ? {
                    id: data.project.client.id,
                    name: data.project.client.name,
                  }
                : null,
              contact: data.project.contact
                ? {
                    id: data.project.contact.id,
                    name: data.project.contact.name,
                    email: data.project.contact.email,
                    phoneNumber: data.project.contact.phone_number,
                  }
                : null,
            }
          : null,
      };
    } catch (error) {
      console.error("Error fetching discrepancy:", error);
      throw error;
    }
  }

  /**
   * Create a new discrepancy
   * @param {object} discrepancyData - The discrepancy data
   * @param {string} origin - Optional origin URL for generating share link
   * @returns {Promise<object>} Created discrepancy
   */
  static async createDiscrepancy(discrepancyData, origin = null) {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      // Validate discrepancy type
      const validTypes = this.getDiscrepancyTypes();
      if (!validTypes.includes(discrepancyData.discrepancyType)) {
        throw new Error(`Invalid discrepancy type: "${discrepancyData.discrepancyType}". Valid types are: ${validTypes.join(', ')}`);
      }

      // Validate status if provided
      if (discrepancyData.discrepancyStatus) {
        const validStatuses = this.getDiscrepancyStatuses();
        if (!validStatuses.includes(discrepancyData.discrepancyStatus)) {
          throw new Error(`Invalid discrepancy status: "${discrepancyData.discrepancyStatus}". Valid statuses are: ${validStatuses.join(', ')}`);
        }
      }

      const dbData = {
        name: discrepancyData.discrepancyName,
        status: discrepancyData.discrepancyStatus || "Draft",
        type: discrepancyData.discrepancyType,
        project_id: discrepancyData.projectId,
        // file_share_link removed - now using markup_attachment for files
        loom_link: discrepancyData.loomLink,
        additional_information:
          discrepancyData.additionalInformation || discrepancyData.description,
        markup_attachment: cleanAttachmentData(discrepancyData.markupAttachment),
        created_by_name: user?.email,
        // Note: removed fields that don't exist in database:
        // location, reported_by, assigned_to, priority, resolution, notes, attachments
        // additional_contact_ids (this might need special handling)
        // file_formats_needed removed - this field only exists in projects table, not discrepancies
      };

      // Debug logging
      console.log("Creating discrepancy with data:", {
        ...dbData,
        typeValue: dbData.type,
        typeType: typeof dbData.type,
        validTypes: this.getDiscrepancyTypes(),
        isValidType: this.getDiscrepancyTypes().includes(dbData.type)
      });

      // Debug logging for file uploads
      if (
        discrepancyData.markupAttachment &&
        discrepancyData.markupAttachment.length > 0
      ) {
      }

      const { data, error } = await supabase
        .from("discrepancies")
        .insert([dbData])
        .select()
        .single();

      if (error) {
        console.error("Supabase error details:", error);
        console.error("Error code:", error.code);
        console.error("Error details:", error.details);
        console.error("Error hint:", error.hint);
        throw new Error(`Failed to create discrepancy: ${error.message}`);
      }

      // Generate and save share URL immediately after creation if origin provided
      if (origin) {
        try {
          const shareUrl = `${origin}?page=discrepancies&discrepancy=${data.id}`;
          await this.updateShareUrl(data.id, shareUrl);
        } catch (shareError) {
          console.warn('Failed to generate share URL during creation:', shareError);
          // Don't fail the whole creation if share URL fails
        }
      }

      return this.getDiscrepancyById(data.id);
    } catch (error) {
      console.error("Error creating discrepancy:", error);
      throw error;
    }
  }

  /**
   * Update a discrepancy
   * @param {number} discrepancyId - The discrepancy ID
   * @param {object} updates - The fields to update
   * @returns {Promise<object>} Updated discrepancy
   */
  static async updateDiscrepancy(discrepancyId, updates) {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      const dbUpdates = {
        last_modified_by: user?.email,
        // Note: updated_at might not exist, will be handled by database triggers
      };

      // Map camelCase to snake_case - only for columns that exist
      if (updates.discrepancyName !== undefined)
        dbUpdates.name = updates.discrepancyName;
      if (updates.discrepancyStatus !== undefined)
        dbUpdates.status = updates.discrepancyStatus;
      // Also support direct status field (for compatibility)
      if (updates.status !== undefined)
        dbUpdates.status = updates.status;
      if (updates.discrepancyType !== undefined)
        dbUpdates.type = updates.discrepancyType;
      if (updates.projectId !== undefined)
        dbUpdates.project_id = updates.projectId;
      // file_share_link removed - now using markup_attachment for files
      if (updates.loomLink !== undefined)
        dbUpdates.loom_link = updates.loomLink;
      if (updates.markupAttachment !== undefined)
        dbUpdates.markup_attachment = cleanAttachmentData(updates.markupAttachment);
      if (updates.shareUrl !== undefined)
        dbUpdates.share_url = updates.shareUrl;
      // Map both additionalInformation and description to additional_information column
      if (updates.additionalInformation !== undefined)
        dbUpdates.additional_information = updates.additionalInformation;
      if (updates.description !== undefined)
        dbUpdates.additional_information = updates.description;
      // Include email fields if explicitly passed (for preserving sent emails)
      if (updates.emailPreview !== undefined)
        dbUpdates.email_preview = updates.emailPreview;
      if (updates.sendEmail !== undefined)
        dbUpdates.send_email = updates.sendEmail;
      if (updates.putProjectOnHold !== undefined)
        dbUpdates.put_project_on_hold = updates.putProjectOnHold;
      if (updates.holdReason !== undefined)
        dbUpdates.hold_reason = updates.holdReason;
      if (updates.holdNotes !== undefined)
        dbUpdates.hold_notes = updates.holdNotes;
      if (updates.holdInitialFollowUpDays !== undefined)
        dbUpdates.hold_initial_follow_up_days = updates.holdInitialFollowUpDays;
      if (updates.holdRecurringFollowUpDays !== undefined)
        dbUpdates.hold_recurring_follow_up_days = updates.holdRecurringFollowUpDays;
      if (updates.emailTo !== undefined)
        dbUpdates.email_to = updates.emailTo;
      if (updates.emailCc !== undefined)
        dbUpdates.email_cc = updates.emailCc;
      if (updates.emailSubject !== undefined)
        dbUpdates.email_subject = updates.emailSubject;
      // Support sent_date field for marking discrepancies as sent
      if (updates.sent_date !== undefined)
        dbUpdates.sent_date = updates.sent_date;
      if (updates.sentDate !== undefined)
        dbUpdates.sent_date = updates.sentDate;

      // Note: removed fields that don't exist in database:
      // location, reported_by, assigned_to, priority, resolution, notes, attachments, additional_contact_ids

      const { data, error } = await supabase
        .from("discrepancies")
        .update(dbUpdates)
        .eq("id", discrepancyId)
        .select()
        .single();

      if (error) {
        throw new Error(`Failed to update discrepancy: ${error.message}`);
      }

      return this.getDiscrepancyById(data.id);
    } catch (error) {
      console.error("Error updating discrepancy:", error);
      throw error;
    }
  }

  /**
   * Delete a discrepancy
   * @param {number} discrepancyId - The discrepancy ID
   * @returns {Promise<boolean>} Success status
   */
  static async deleteDiscrepancy(discrepancyId) {
    try {
      const { error } = await supabase
        .from("discrepancies")
        .delete()
        .eq("id", discrepancyId);

      if (error) {
        throw new Error(`Failed to delete discrepancy: ${error.message}`);
      }

      return true;
    } catch (error) {
      console.error("Error deleting discrepancy:", error);
      throw error;
    }
  }

  /**
   * Get available discrepancy types (enum values)
   * @returns {Array<string>} Array of discrepancy types
   */
  static getDiscrepancyTypes() {
    // These match the database enum values
    return [
      "PDF/CAD File Discrepancy",
      "Missing Linework",
      "Revision Overlays",
      "Grading Clarification",
      "Utility Discrepancy",
      "Profile Discrepancy",
      "Missing CAD Files",
    ];
  }

  /**
   * Get available discrepancy statuses (enum values)
   * @returns {Array<string>} Array of discrepancy statuses
   */
  static getDiscrepancyStatuses() {
    // These match the database enum values and board grouping order
    return [
      "Draft",                  // 1 - Initial state
      "Waiting to Review",      // 2 - Ready for review
      "Sent",                   // 3 - Email sent to client
      "Reviewed - not sent",    // 4 - Reviewed but not emailed
      "Resolved",               // 5 - Issue resolved
    ];
  }

  /**
   * Get priority levels
   * @returns {Array<string>} Array of priority levels
   */
  static getPriorityLevels() {
    return ["Low", "Medium", "High", "Critical"];
  }

  /**
   * Get contacts for a specific client
   * @param {number} clientId - The client ID
   * @returns {Promise<Array>} Array of contacts for the client
   */
  static async getContactsByClientId(clientId) {
    try {
      const { data, error } = await supabase
        .from("contacts")
        .select("id, name, email, phone_number, role")
        .eq("client_id", clientId)
        .order("name");

      if (error) {
        throw new Error(`Failed to fetch contacts: ${error.message}`);
      }

      return data || [];
    } catch (error) {
      console.error("Error fetching contacts:", error);
      throw error;
    }
  }

  /**
   * Get project with full details including contacts
   * @param {number} projectId - The project ID
   * @returns {Promise<object>} Project with client and primary contact details
   */
  static async getProjectWithContacts(projectId) {
    try {
      const { data, error } = await supabase
        .from("projects")
        .select(
          `
          id,
          name,
          job_number,
          rev,
          modeling_status,
          modeling_state,
          client_id,
          contact_id,
          clients (
            id,
            name
          ),
          contacts (
            id,
            name,
            email,
            phone_number,
            role
          )
        `,
        )
        .eq("id", projectId)
        .single();

      if (error) {
        throw new Error(`Failed to fetch project: ${error.message}`);
      }

      return data;
    } catch (error) {
      console.error("Error fetching project:", error);
      throw error;
    }
  }

  /**
   * Get email template for a specific discrepancy type
   * @param {string} discrepancyType - The type of discrepancy
   * @returns {Promise<string>} The email template text
   */
  static async getEmailTemplate(discrepancyType) {
    try {
      // Ensure the discrepancy type exactly matches the enum values
      const validTypes = this.getDiscrepancyTypes();
      if (!validTypes.includes(discrepancyType)) {
        console.warn(`Invalid discrepancy type: ${discrepancyType}. Valid types are:`, validTypes);
        return "";
      }

      const { data, error } = await supabase
        .from("discrepancy_and_clarification_type")
        .select("email_body_text")
        .eq("name", discrepancyType)
        .single();

      if (error) {
        console.error("Error fetching email template:", error);
        return "";
      }

      return data?.email_body_text || "";
    } catch (error) {
      console.error("Error in getEmailTemplate:", error);
      return "";
    }
  }

  /**
   * Update discrepancy with email preview
   * @param {number} discrepancyId - The discrepancy ID
   * @param {string} emailPreview - The email preview text
   * @returns {Promise<Object>} Updated discrepancy
   */
  static async updateEmailPreview(discrepancyId, emailPreview) {
    try {
      const { data, error } = await supabase
        .from("discrepancies")
        .update({ email_preview: emailPreview })
        .eq("id", discrepancyId)
        .select()
        .single();

      if (error) {
        throw new Error(`Failed to update email preview: ${error.message}`);
      }

      return data;
    } catch (error) {
      console.error("Error updating email preview:", error);
      throw error;
    }
  }

  /**
   * Send discrepancy email (sets send_email flag to false then true to allow re-triggering)
   * @param {number} discrepancyId - The discrepancy ID
   * @param {string} emailPreview - The email preview text to save
   * @param {Array} emailTo - Array of contact IDs for To field
   * @param {Array} emailCc - Array of contact IDs for CC field
   * @param {string} emailSubject - The email subject line
   * @param {string} emailHtml - The email HTML content
   * @returns {Promise<Object>} Updated discrepancy
   */
  static async sendDiscrepancyEmail(discrepancyId, emailPreview, emailTo = [], emailCc = [], emailSubject = "", emailHtml = "") {
    try {
      // Validate payload sizes to prevent "payload string too long" errors
      const maxTextSize = 64000; // ~64KB limit for text fields
      const maxHtmlSize = 512000; // ~512KB limit for HTML content

      if (emailPreview && emailPreview.length > maxTextSize) {
        throw new Error(`Email content is too large (${emailPreview.length} characters). Maximum allowed is ${maxTextSize} characters.`);
      }

      if (emailHtml && emailHtml.length > maxHtmlSize) {
        throw new Error(`Email HTML is too large (${emailHtml.length} characters). Maximum allowed is ${maxHtmlSize} characters.`);
      }

      if (emailSubject && emailSubject.length > 255) {
        throw new Error(`Email subject is too long (${emailSubject.length} characters). Maximum allowed is 255 characters.`);
      }

      // Get the current logged-in user
      const {
        data: { user },
      } = await supabase.auth.getUser();
      // First set send_email to false to reset the trigger
      await supabase
        .from("discrepancies")
        .update({
          send_email: false,
        })
        .eq("id", discrepancyId);
      // Then set it to true along with other updates to trigger the n8n workflow
      const { data, error } = await supabase
        .from("discrepancies")
        .update({
          email_preview: emailPreview,
          email_html: emailHtml,
          email_to: emailTo,
          email_cc: emailCc,
          email_subject: emailSubject,
          sent_date: new Date().toISOString(),
          status: "Sent",
          send_email: true,
          last_modified_by: user?.email,
        })
        .eq("id", discrepancyId)
        .select()
        .single();

      if (error) {
        console.error('[DiscrepancyService] Failed to update discrepancy:', error);
        throw new Error(`Failed to send email: ${error.message}`);
      }
      return data;
    } catch (error) {
      console.error("Error sending discrepancy email:", error);
      throw error;
    }
  }

  /**
   * Update discrepancy share URL
   * @param {number} discrepancyId - The discrepancy ID
   * @param {string} shareUrl - The share URL to save
   * @returns {Promise<Object>} Updated discrepancy
   */
  static async updateShareUrl(discrepancyId, shareUrl) {
    try {
      const { data, error } = await supabase
        .from("discrepancies")
        .update({ share_url: shareUrl })
        .eq("id", discrepancyId)
        .select()
        .single();

      if (error) {
        throw new Error(`Failed to update share URL: ${error.message}`);
      }

      return data;
    } catch (error) {
      console.error("Error updating share URL:", error);
      throw error;
    }
  }

  /**
   * Get discrepancies for a specific project
   * @param {number} projectId - The project ID
   * @returns {Promise<Array>} Array of discrepancies for the project
   */
  static async getDiscrepanciesByProjectId(projectId) {
    try {
      const { data, error } = await supabase
        .from("discrepancies")
        .select(
          `
          *,
          markup_attachment,
          file_share_link,
          share_url,
          loom_link,
          created_by_name,
          sent_date,
          email_preview,
          additional_information
        `
        )
        .eq("project_id", projectId)
        .order("created_at", { ascending: false });

      if (error) {
        throw new Error(`Failed to fetch discrepancies: ${error.message}`);
      }

      // Get unique creator emails to look up names
      // Check both created_by and created_by_name fields for emails
      const creatorEmails = [...new Set(
        (data || [])
          .map(item => {
            // Prefer created_by if it's an email, otherwise use created_by_name
            const email = item.created_by && item.created_by.includes('@')
              ? item.created_by
              : item.created_by_name;
            return email;
          })
          .filter(email => email && email.includes('@'))
      )];

      // Lookup employee names if there are any emails
      let employeeNameMap = {};
      if (creatorEmails.length > 0) {
        try {
          const { data: employees } = await supabase
            .from('employees')
            .select('email, name')
            .in('email', creatorEmails);

          if (employees) {
            employeeNameMap = Object.fromEntries(
              employees.map(emp => [emp.email, emp.name])
            );
          }
        } catch (lookupError) {
          console.warn('Could not lookup employee names:', lookupError);
        }
      }

      // Transform the data to match the format used in the application
      return (data || []).map((item) => ({
        id: item.id,
        discrepancyName: item.name || "Unnamed Discrepancy",
        discrepancyStatus: item.status,
        discrepancyType: item.type,
        description: item.additional_information,
        additionalInformation: item.additional_information,
        projectId: item.project_id,
        markupAttachment: item.markup_attachment,
        fileShareLink: item.file_share_link,
        shareUrl: item.share_url,
        loomLink: item.loom_link,
        // Use name from employees lookup if available, otherwise fall back to created_by_name field or email
        // Determine which email field to use for lookup (same logic as above)
        createdByName: (() => {
          const email = item.created_by && item.created_by.includes('@')
            ? item.created_by
            : item.created_by_name;
          return employeeNameMap[email] || item.created_by_name || item.created_by;
        })(),
        sentDate: item.sent_date,
        emailPreview: item.email_preview,
        createdAt: item.created_at,
        updatedAt: item.updated_at,
        createdBy: item.created_by,
        updatedBy: item.updated_by,
      }));
    } catch (error) {
      console.error("Error fetching discrepancies by project ID:", error);
      throw error;
    }
  }

  /**
   * Create a new discrepancy and immediately send email (for Send Board workflow)
   * @param {Object} discrepancyData - The discrepancy data
   * @param {string} discrepancyData.discrepancyType - Type of discrepancy
   * @param {string} discrepancyData.projectId - Project ID
   * @param {string} discrepancyData.additionalInformation - Description/details
   * @param {Array} discrepancyData.markupAttachment - File attachments
   * @param {Array} discrepancyData.emailTo - Email To recipients (contact IDs)
   * @param {Array} discrepancyData.emailCc - Email CC recipients (contact IDs)
   * @param {string} discrepancyData.emailSubject - Email subject
   * @param {string} discrepancyData.emailPreview - Email body content
   * @returns {Promise<Object>} Created and sent discrepancy
   */
  static async createAndSendDiscrepancy(discrepancyData) {
    try {
      // Validate payload sizes to prevent "payload string too long" errors
      const maxTextSize = 64000; // ~64KB limit for text fields
      const maxHtmlSize = 512000; // ~512KB limit for HTML content
      
      if (discrepancyData.emailPreview && discrepancyData.emailPreview.length > maxTextSize) {
        throw new Error(`Email content is too large (${discrepancyData.emailPreview.length} characters). Maximum allowed is ${maxTextSize} characters.`);
      }
      
      if (discrepancyData.emailHtml && discrepancyData.emailHtml.length > maxHtmlSize) {
        throw new Error(`Email HTML is too large (${discrepancyData.emailHtml.length} characters). Maximum allowed is ${maxHtmlSize} characters.`);
      }
      
      if (discrepancyData.emailSubject && discrepancyData.emailSubject.length > 255) {
        throw new Error(`Email subject is too long (${discrepancyData.emailSubject.length} characters). Maximum allowed is 255 characters.`);
      }

      // Get the current logged-in user
      const {
        data: { user },
      } = await supabase.auth.getUser();

      // Validate discrepancy type
      const validTypes = this.getDiscrepancyTypes();
      if (!validTypes.includes(discrepancyData.discrepancyType)) {
        throw new Error(`Invalid discrepancy type: "${discrepancyData.discrepancyType}". Valid types are: ${validTypes.join(', ')}`);
      }

      // Create the discrepancy with "Sent" status and email data
      const discrepancyToCreate = {
        type: discrepancyData.discrepancyType,
        project_id: discrepancyData.projectId,
        additional_information: discrepancyData.additionalInformation,
        markup_attachment: cleanAttachmentData(discrepancyData.markupAttachment),
        status: "Sent", // Start as sent since we're sending immediately
        created_by: user?.email,
        created_by_name: user?.email,
        sent_date: new Date().toISOString(),
        email_preview: discrepancyData.emailPreview,
        email_to: discrepancyData.emailTo,
        email_cc: discrepancyData.emailCc,
        email_subject: discrepancyData.emailSubject,
        send_email: false, // Start as false, will set to true after creation
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      // Create the discrepancy
      const { data: createdDiscrepancy, error: createError } = await supabase
        .from("discrepancies")
        .insert([discrepancyToCreate])
        .select()
        .single();

      if (createError) {
        console.error("Error creating discrepancy:", createError);
        throw createError;
      }
      // Now trigger the email send by setting send_email to true
      const { data: updatedDiscrepancy, error: updateError } = await supabase
        .from("discrepancies")
        .update({
          send_email: true,
          last_modified_by: user?.email,
        })
        .eq("id", createdDiscrepancy.id)
        .select()
        .single();

      if (updateError) {
        console.error("Error triggering email send:", updateError);
        // Don't throw here since discrepancy was created successfully
        console.warn("Discrepancy created but email send may have failed");
        return createdDiscrepancy;
      }
      return updatedDiscrepancy;
    } catch (error) {
      console.error("Error in createAndSendDiscrepancy:", error);
      throw new Error(
        `Failed to create and send discrepancy: ${error.message || "Unknown error"}`,
      );
    }
  }

  /**
   * Get discrepancies for multiple projects efficiently
   * @param {number[]} projectIds - Array of project IDs
   * @returns {Promise<Object>} Map of projectId to discrepancy array
   */
  static async getDiscrepanciesByProjectIds(projectIds) {
    try {
      if (!projectIds || projectIds.length === 0) {
        return {};
      }

      const { data, error } = await supabase
        .from("discrepancies")
        .select(
          `
          id,
          project_id,
          name,
          status,
          type,
          created_at,
          sent_date,
          created_by_name
        `
        )
        .in("project_id", projectIds)
        .order("created_at", { ascending: false });

      if (error) {
        throw new Error(`Failed to fetch discrepancies: ${error.message}`);
      }

      // Group discrepancies by project_id
      const discrepanciesByProject = {};
      
      // Initialize all projects with empty arrays
      projectIds.forEach(projectId => {
        discrepanciesByProject[projectId] = [];
      });

      // Group the actual discrepancies
      (data || []).forEach((item) => {
        if (!discrepanciesByProject[item.project_id]) {
          discrepanciesByProject[item.project_id] = [];
        }
        
        discrepanciesByProject[item.project_id].push({
          id: item.id,
          discrepancyName: item.name || "Unnamed Discrepancy",
          discrepancyStatus: item.status,
          discrepancyType: item.type,
          projectId: item.project_id,
          createdAt: item.created_at,
          sentDate: item.sent_date,
          createdByName: item.created_by_name,
        });
      });

      return discrepanciesByProject;
    } catch (error) {
      console.error("Error fetching discrepancies by project IDs:", error);
      throw error;
    }
  }
}
